import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProjectCalculator = ({ isOpen, onClose, serviceId }) => {
  const [formData, setFormData] = useState({
    projectType: '',
    complexity: '',
    timeline: '',
    teamSize: '',
    features: [],
    budget: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const projectTypes = [
    { value: 'web-app', label: 'Web Application' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'desktop-app', label: 'Desktop Application' },
    { value: 'api-service', label: 'API Service' },
    { value: 'integration', label: 'System Integration' },
    { value: 'migration', label: 'Legacy Migration' }
  ];

  const complexityLevels = [
    { value: 'simple', label: 'Simple - Basic CRUD operations' },
    { value: 'moderate', label: 'Moderate - Multiple integrations' },
    { value: 'complex', label: 'Complex - Advanced features' },
    { value: 'enterprise', label: 'Enterprise - Large scale system' }
  ];

  const timelineOptions = [
    { value: '2-4', label: '2-4 weeks' },
    { value: '1-3', label: '1-3 months' },
    { value: '3-6', label: '3-6 months' },
    { value: '6-12', label: '6-12 months' },
    { value: '12+', label: '12+ months' }
  ];

  const teamSizeOptions = [
    { value: '1-2', label: '1-2 developers' },
    { value: '3-5', label: '3-5 developers' },
    { value: '6-10', label: '6-10 developers' },
    { value: '10+', label: '10+ developers' }
  ];

  const featureOptions = [
    { value: 'auth', label: 'User Authentication' },
    { value: 'payments', label: 'Payment Processing' },
    { value: 'analytics', label: 'Analytics Dashboard' },
    { value: 'notifications', label: 'Push Notifications' },
    { value: 'api', label: 'REST API' },
    { value: 'admin', label: 'Admin Panel' },
    { value: 'chat', label: 'Real-time Chat' },
    { value: 'search', label: 'Advanced Search' }
  ];

  const calculateEstimate = () => {
    setIsCalculating(true);
    
    // Simulate calculation
    setTimeout(() => {
      const baseRates = {
        'simple': 5000,
        'moderate': 15000,
        'complex': 35000,
        'enterprise': 75000
      };

      const timelineMultipliers = {
        '2-4': 0.8,
        '1-3': 1.0,
        '3-6': 1.2,
        '6-12': 1.5,
        '12+': 2.0
      };

      const teamMultipliers = {
        '1-2': 1.0,
        '3-5': 1.3,
        '6-10': 1.8,
        '10+': 2.5
      };

      const basePrice = baseRates[formData.complexity] || 15000;
      const timelineMultiplier = timelineMultipliers[formData.timeline] || 1.0;
      const teamMultiplier = teamMultipliers[formData.teamSize] || 1.0;
      const featureMultiplier = 1 + (formData.features.length * 0.15);

      const totalEstimate = Math.round(basePrice * timelineMultiplier * teamMultiplier * featureMultiplier);
      const duration = formData.timeline || '1-3 months';
      const team = formData.teamSize || '3-5 developers';

      setEstimate({
        total: totalEstimate,
        duration,
        team,
        breakdown: {
          development: Math.round(totalEstimate * 0.6),
          design: Math.round(totalEstimate * 0.2),
          testing: Math.round(totalEstimate * 0.15),
          management: Math.round(totalEstimate * 0.05)
        }
      });
      setIsCalculating(false);
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-brand max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">Project Calculator</h2>
            <p className="text-text-secondary">Get an instant estimate for your project</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
          />
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="space-y-6">
              <Select
                label="Project Type"
                options={projectTypes}
                value={formData.projectType}
                onChange={(value) => handleInputChange('projectType', value)}
                placeholder="Select project type"
              />

              <Select
                label="Complexity Level"
                options={complexityLevels}
                value={formData.complexity}
                onChange={(value) => handleInputChange('complexity', value)}
                placeholder="Select complexity"
              />

              <Select
                label="Expected Timeline"
                options={timelineOptions}
                value={formData.timeline}
                onChange={(value) => handleInputChange('timeline', value)}
                placeholder="Select timeline"
              />

              <Select
                label="Team Size"
                options={teamSizeOptions}
                value={formData.teamSize}
                onChange={(value) => handleInputChange('teamSize', value)}
                placeholder="Select team size"
              />

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Required Features
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {featureOptions.map((feature) => (
                    <button
                      key={feature.value}
                      onClick={() => handleFeatureToggle(feature.value)}
                      className={`p-3 text-sm rounded-brand border transition-brand text-left ${
                        formData.features.includes(feature.value)
                          ? 'bg-secondary/10 border-secondary text-secondary' :'bg-muted border-border text-text-secondary hover:border-secondary/50'
                      }`}
                    >
                      {feature.label}
                    </button>
                  ))}
                </div>
              </div>

              <Input
                label="Budget Range (Optional)"
                type="text"
                placeholder="e.g., $10,000 - $50,000"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
              />

              <Button
                variant="default"
                fullWidth
                iconName="Calculator"
                iconPosition="left"
                onClick={calculateEstimate}
                disabled={!formData.projectType || !formData.complexity || isCalculating}
                loading={isCalculating}
                className="cta-primary"
              >
                {isCalculating ? 'Calculating...' : 'Calculate Estimate'}
              </Button>
            </div>

            {/* Results */}
            <div className="bg-muted rounded-brand p-6">
              {estimate ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-secondary mb-2">
                      ${estimate.total.toLocaleString()}
                    </div>
                    <div className="text-text-secondary">Estimated Project Cost</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-background rounded-brand">
                      <Icon name="Clock" size={20} className="mx-auto mb-2 text-secondary" />
                      <div className="font-medium text-text-primary">{estimate.duration}</div>
                      <div className="text-xs text-text-secondary">Duration</div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-brand">
                      <Icon name="Users" size={20} className="mx-auto mb-2 text-secondary" />
                      <div className="font-medium text-text-primary">{estimate.team}</div>
                      <div className="text-xs text-text-secondary">Team Size</div>
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div>
                    <h4 className="font-semibold text-text-primary mb-3">Cost Breakdown</h4>
                    <div className="space-y-2">
                      {Object.entries(estimate.breakdown).map(([category, amount]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="text-text-secondary capitalize">{category}</span>
                          <span className="font-medium text-text-primary">
                            ${amount.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <Button
                      variant="default"
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                      className="cta-conversion"
                    >
                      Discuss This Estimate
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Calculator" size={48} className="mx-auto mb-4 text-text-secondary" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Ready to Calculate?
                  </h3>
                  <p className="text-text-secondary">
                    Fill out the form to get your project estimate
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCalculator;